import { account } from '../lib/appwrite';
import { ID, OAuthProvider } from 'appwrite';
import { getOrCreateProfile } from './profileService';
import { isOAuthSafe, detectBrowserEnvironment } from '../utils/browserDetection';
import { requestEmailVerification, isEmailVerified } from './emailVerificationService';

// ============================================
// APPWRITE AUTH SERVICE
// ============================================

// Cache to prevent multiple simultaneous getCurrentUser calls
let currentUserPromise = null;

// Session duration constants (in seconds)
const SESSION_DURATION_REMEMBERED = 60 * 60 * 24 * 60; // 60 days
const SESSION_DURATION_DEFAULT = 60 * 60 * 24; // 1 day (session-like)

/**
 * Store remember me preference in localStorage
 * @param {boolean} rememberMe - Whether to remember the user
 */
const storeRememberPreference = (rememberMe) => {
  if (rememberMe) {
    localStorage.setItem('rememberMe', 'true');
    localStorage.setItem('sessionExpiry', Date.now() + (SESSION_DURATION_REMEMBERED * 1000));
  } else {
    sessionStorage.setItem('rememberMe', 'false');
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('sessionExpiry');
  }
};

/**
 * Check if session should be considered expired based on remember me preference
 * @returns {boolean} - Whether the session is expired
 */
export const isSessionExpired = () => {
  const rememberMe = localStorage.getItem('rememberMe');
  
  if (rememberMe === 'true') {
    const expiry = localStorage.getItem('sessionExpiry');
    if (expiry && Date.now() > parseInt(expiry)) {
      return true; // 60 days have passed
    }
    return false;
  }
  
  // IMPORTANT: Don't expire session just because sessionStorage was cleared
  // sessionStorage can be cleared by redirects (like from Stripe)
  // Trust Appwrite's own session management instead
  // Only check sessionStorage if it explicitly says 'false'
  const sessionActive = sessionStorage.getItem('rememberMe');
  if (sessionActive === 'false') {
    // User explicitly chose not to be remembered in this session
    // But don't expire if we have an active Appwrite session
    // Let Appwrite handle the session expiry
    return false;
  }
  
  return false; // Default to not expired, let Appwrite manage sessions
};

/**
 * Create a new user account with email and password
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @param {string} name - User's display name
 * @param {boolean} rememberMe - Whether to remember the user
 * @returns {Promise<Object>} - Created user object
 */
export const createAccount = async (email, password, name = '', rememberMe = true) => {
  try {
    console.log('Creating account for:', email, 'with name:', name);
    const user = await account.create(ID.unique(), email, password, name);
    console.log('User account created:', user);
    
    // Send verification email instead of auto-login
    try {
      await requestEmailVerification(user.$id, email);
      console.log('Verification email sent to:', email);
    } catch (emailError) {
      console.error('Failed to send verification email:', emailError);
      // Don't throw - account was created, just email failed
    }
    
    // DO NOT auto-login or create profile until email is verified
    // User must verify email first
    
    return user;
  } catch (error) {
    console.error('Create account error:', error);
    throw error;
  }
};

/**
 * Login with email and password
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @param {boolean} rememberMe - Whether to remember the user for 60 days
 * @returns {Promise<Object>} - Session object
 */
export const loginWithEmail = async (email, password, rememberMe = true) => {
  try {
    // Create session first to get user ID
    const session = await account.createEmailPasswordSession(email, password);
    
    // Check if email is verified
    const verified = await isEmailVerified(session.userId);
    if (!verified) {
      // Delete the session since user is not verified
      await account.deleteSession('current');
      const error = new Error('Please verify your email before logging in. Check your inbox for the verification link.');
      error.code = 'EMAIL_NOT_VERIFIED';
      throw error;
    }
    
    storeRememberPreference(rememberMe);
    return session;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Login with Google OAuth
 * Opens Google login in a popup window or redirects based on environment
 * @param {boolean} rememberMe - Whether to remember the user for 60 days
 * @throws {Error} If called from an in-app browser/WebView
 */
export const loginWithGoogle = (rememberMe = true) => {
  try {
    // Check if we're in a WebView or in-app browser
    const browserEnv = detectBrowserEnvironment();
    if (browserEnv.isInAppBrowser) {
      const error = new Error('OAuth not supported in in-app browsers');
      error.code = 'WEBVIEW_DETECTED';
      error.browserName = browserEnv.browserName;
      error.userAgent = browserEnv.userAgent;
      throw error;
    }
    
    // Store preference before OAuth (will be applied after callback)
    sessionStorage.setItem('pendingRememberMe', rememberMe ? 'true' : 'false');
    
    // Build the OAuth URL
    const endpoint = process.env.REACT_APP_APPWRITE_ENDPOINT;
    const projectId = process.env.REACT_APP_APPWRITE_PROJECT_ID;
    const successUrl = encodeURIComponent(`${window.location.origin}/oauth-callback`);
    const failureUrl = encodeURIComponent(`${window.location.origin}/login`);
    
    const oauthUrl = `${endpoint}/account/sessions/oauth2/google?project=${projectId}&success=${successUrl}&failure=${failureUrl}`;
    
    // Detect mobile devices
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      // On mobile, use redirect instead of popup for better compatibility
      window.location.href = oauthUrl;
      return;
    }
    
    // Desktop: Use popup window
    const width = 500;
    const height = 600;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    // Open OAuth in popup window
    const popup = window.open(
      oauthUrl,
      'Google Sign In',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
    );
    
    // Check if popup was blocked
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      // Popup blocked, fall back to redirect
      console.warn('Popup blocked, redirecting...');
      window.location.href = oauthUrl;
      return;
    }
    
    // Listen for messages from popup
    const handleMessage = (event) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'OAUTH_SUCCESS') {
        // OAuth succeeded, reload to update auth state
        window.location.reload();
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    // Fallback: Monitor popup - if it closes without message, still reload
    if (popup) {
      const checkPopup = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopup);
          window.removeEventListener('message', handleMessage);
          // Trigger a page reload to update auth state
          setTimeout(() => window.location.reload(), 100);
        }
      }, 500);
    }
  } catch (error) {
    console.error('Google login error:', error);
    throw error;
  }
};

/**
 * Get the current logged-in user
 * Also handles pending rememberMe preference from OAuth redirects
 * @returns {Promise<Object|null>} - User object or null if not logged in
 */
export const getCurrentUser = async () => {
  // 0. Check if session should be considered expired based on local hints BEFORE making network call
  // This prevents noisy 401 errors for sessions we already know are expired
  if (isSessionExpired()) {
    console.log('Session expired based on local hints, skipping account.get()');
    return null;
  }

  // If there's already a getCurrentUser call in progress, return that promise
  if (currentUserPromise) {
    console.log('getCurrentUser already in progress, returning cached promise');
    return await currentUserPromise;
  }
  
  // Create and cache the promise
  currentUserPromise = (async () => {
    try {
      const user = await account.get();
      
      // Check if there's a pending rememberMe preference from OAuth login
      const pendingRememberMe = sessionStorage.getItem('pendingRememberMe');
      if (pendingRememberMe !== null) {
        storeRememberPreference(pendingRememberMe === 'true');
        sessionStorage.removeItem('pendingRememberMe');
      }
      
      // Determine if this is an OAuth user
      // OAuth users have emailVerification set to true automatically by Appwrite
      // They also typically don't have a password hash set
      const isOAuthUser = user.emailVerification === true;
      
      // For OAuth users, profile is created immediately (they're already verified by OAuth provider)
      // For email/password users, profile is only created after email verification
      // If Appwrite says email is verified, trust it (covers both OAuth and manual verification)
      const shouldCreateProfile = isOAuthUser || await isEmailVerified(user.$id);
      
      if (shouldCreateProfile) {
        try {
          console.log('Checking/creating profile for user:', user.$id, 'emailVerified:', user.emailVerification);
          const profile = await getOrCreateProfile(user.$id, user.name || user.email.split('@')[0], user.email);
          console.log('Profile check/creation result:', profile);
        } catch (profileError) {
          console.error('Profile check/creation error:', profileError);
          console.error('Profile error details:', profileError.message, profileError.code);
          // Continue even if profile operation fails
        }
      } else {
        console.log('Email not verified yet for user:', user.$id, '- profile creation skipped');
      }
      
      return user;
    } catch (error) {
      // User is not logged in or session is invalid (401)
      if (error.code === 401) {
        console.log('Session invalid (401), clearing local state hints');
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('sessionExpiry');
        sessionStorage.removeItem('rememberMe');
      }
      return null;
    } finally {
      // Clear the cache after the call completes
      currentUserPromise = null;
    }
  })();
  
  return await currentUserPromise;
};

/**
 * Logout the current user
 * @returns {Promise<void>}
 */
export const logout = async () => {
  try {
    await account.deleteSession('current');
    // Clear remember me preferences
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('sessionExpiry');
    sessionStorage.removeItem('rememberMe');
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

/**
 * Logout from all sessions
 * @returns {Promise<void>}
 */
export const logoutAll = async () => {
  try {
    await account.deleteSessions();
  } catch (error) {
    console.error('Logout all error:', error);
    throw error;
  }
};

/**
 * Send password recovery email
 * @param {string} email - User's email
 * @returns {Promise<Object>}
 */
export const sendPasswordRecovery = async (email) => {
  try {
    const result = await account.createRecovery(
      email,
      `${window.location.origin}/reset-password`
    );
    return result;
  } catch (error) {
    console.error('Password recovery error:', error);
    throw error;
  }
};

/**
 * Complete password recovery
 * @param {string} userId - User ID from recovery link
 * @param {string} secret - Secret from recovery link
 * @param {string} password - New password
 * @returns {Promise<Object>}
 */
export const confirmPasswordRecovery = async (userId, secret, password) => {
  try {
    const result = await account.updateRecovery(userId, secret, password);
    return result;
  } catch (error) {
    console.error('Confirm password recovery error:', error);
    throw error;
  }
};

/**
 * Send email verification
 * @returns {Promise<Object>}
 */
export const sendEmailVerification = async () => {
  try {
    const result = await account.createVerification(
      `${window.location.origin}/verify-email`
    );
    return result;
  } catch (error) {
    console.error('Email verification error:', error);
    throw error;
  }
};

/**
 * Confirm email verification
 * @param {string} userId - User ID from verification link
 * @param {string} secret - Secret from verification link
 * @returns {Promise<Object>}
 */
export const confirmEmailVerification = async (userId, secret) => {
  try {
    const result = await account.updateVerification(userId, secret);
    return result;
  } catch (error) {
    console.error('Confirm email verification error:', error);
    throw error;
  }
};

/**
 * Update user's name
 * @param {string} name - New display name
 * @returns {Promise<Object>}
 */
export const updateName = async (name) => {
  try {
    const result = await account.updateName(name);
    return result;
  } catch (error) {
    console.error('Update name error:', error);
    throw error;
  }
};

/**
 * Update user's password
 * @param {string} newPassword - New password
 * @param {string} oldPassword - Current password
 * @returns {Promise<Object>}
 */
export const updatePassword = async (newPassword, oldPassword) => {
  try {
    const result = await account.updatePassword(newPassword, oldPassword);
    return result;
  } catch (error) {
    console.error('Update password error:', error);
    throw error;
  }
};

export default {
  createAccount,
  loginWithEmail,
  loginWithGoogle,
  getCurrentUser,
  logout,
  logoutAll,
  sendPasswordRecovery,
  confirmPasswordRecovery,
  sendEmailVerification,
  confirmEmailVerification,
  updateName,
  updatePassword,
};
